import * as React from 'react';
import { cn } from '@/lib/utils';

export interface OpcaoSegmento<T extends string> {
  valor: T;
  rotulo: React.ReactNode;
  icone?: React.ReactNode | undefined;
  contagem?: number | undefined;
}

/**
 * Controle segmentado com indicador deslizante. Substitui os grupos de
 * botões "ligado/desligado" espalhados pelas telas — dois cliques a menos de
 * hesitação porque o estado atual é inequívoco.
 */
export function Segmentado<T extends string>({
  valor,
  opcoes,
  aoMudar,
  rotuloAcessivel,
  tamanho = 'padrao',
  className,
}: {
  valor: T;
  opcoes: readonly OpcaoSegmento<T>[];
  aoMudar: (valor: T) => void;
  rotuloAcessivel: string;
  tamanho?: 'padrao' | 'pequeno';
  className?: string | undefined;
}): JSX.Element {
  const indice = Math.max(
    0,
    opcoes.findIndex((o) => o.valor === valor),
  );

  return (
    <div
      role="group"
      aria-label={rotuloAcessivel}
      className={cn(
        'relative inline-grid rounded-xl border border-borda bg-superficie2 p-1 shadow-xs',
        className,
      )}
      style={{ gridTemplateColumns: `repeat(${opcoes.length}, minmax(0, 1fr))` }}
    >
      {/* Indicador: uma só peça que desliza, em vez de cada botão acender. */}
      <span
        aria-hidden
        className="pointer-events-none absolute bottom-1 top-1 rounded-lg bg-superficie shadow-cartao ring-1 ring-borda transition-transform duration-300 ease-suave"
        style={{
          width: `calc((100% - 8px) / ${opcoes.length})`,
          transform: `translateX(calc(${indice} * 100%))`,
          left: 4,
        }}
      />
      {opcoes.map((opcao) => {
        const ativo = opcao.valor === valor;
        return (
          <button
            key={opcao.valor}
            type="button"
            aria-pressed={ativo}
            onClick={() => aoMudar(opcao.valor)}
            className={cn(
              'relative z-10 flex items-center justify-center gap-1.5 rounded-lg font-semibold transition-colors',
              tamanho === 'pequeno' ? 'min-h-[32px] px-3 text-2xs' : 'min-h-[38px] px-4 text-[13px]',
              ativo ? 'text-primaria' : 'text-textoFraco hover:text-texto',
            )}
          >
            {opcao.icone}
            {opcao.rotulo}
            {opcao.contagem !== undefined && opcao.contagem > 0 ? (
              <span
                className={cn(
                  'tabular rounded-full px-1.5 text-[10px] font-bold',
                  ativo ? 'bg-primaria-suave text-primaria' : 'bg-superficie3 text-textoFraco',
                )}
              >
                {opcao.contagem}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
